/**
 * WMS Custom Fields — shared types & descriptor table
 *
 * User-definable extra fields ("custom fields") that tenants attach to core WMS
 * entities (Product, Location, Zone, ASN, …) WITHOUT code changes. A field is
 * declared once (a CustomFieldDefinition) and then rendered everywhere: CRUD
 * forms, grids, the handheld/mobile SDUI terminal, and the algorithm/flow engine.
 *
 * This file is the SINGLE SOURCE OF TRUTH (Rule 15 / DRY): the
 * `CUSTOM_FIELD_TYPE_DESCRIPTORS` table maps each `CustomFieldType` to its
 * primitive, SDUI component, grid data type, and algorithm-catalog field type.
 * Adding a new field type means editing this one file — backend validation, web
 * form/grid rendering, the mobile SDUI mapper, and the algorithm catalog merge
 * all derive from this table.
 *
 * Shared between: backend-wms, web-wms, mobile-wms.
 */

import type { SDUIComponentType } from '../sdui/component.types';

import type { AlgorithmFieldType } from './algorithm-engine.types';

// ════════════════════════════════════════════════════════════════════════
// ENUMS
// ════════════════════════════════════════════════════════════════════════

/** The data type of a user-defined custom field. */
export enum CustomFieldType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  BOOLEAN = 'BOOLEAN',
  DATE = 'DATE',
  /** Single-select from a fixed option set. */
  LIST = 'LIST',
  /** Multi-select from a fixed option set (string[]). Not algorithm-exposable. */
  MULTI_LIST = 'MULTI_LIST',
}

/**
 * Core entities that can carry custom fields. Extended as the feature rolls out
 * to more entities; the DB enum (`wms_custom_field_definitions_entity_type_enum`)
 * must be kept in sync via migration.
 */
export enum CustomFieldEntityType {
  PRODUCT = 'PRODUCT',
  LOCATION = 'LOCATION',
  ZONE = 'ZONE',
  ASN = 'ASN',
  /** 3PL customer (financial owner of stock). */
  CLIENT = 'CLIENT',
}

/**
 * Where a custom field renders on a master-detail entity: the header (MASTER),
 * each line (DETAIL), or both. Non-master-detail entities only ever use MASTER.
 * DB enum: `wms_custom_field_definitions_placement_enum`.
 */
export enum CustomFieldPlacement {
  MASTER = 'MASTER',
  DETAIL = 'DETAIL',
  BOTH = 'BOTH',
}

/**
 * Entity types that have a detail (line) level, so a field can target the line.
 * Single source of truth: drives the admin placement picker (only shown for
 * these) and the per-line form section. Add an entry as more master-detail
 * entities adopt custom fields (Receiving Order, Purchase Order…).
 */
export const MASTER_DETAIL_ENTITY_TYPES: Partial<
  Record<CustomFieldEntityType, { lineLabel: string }>
> = {
  [CustomFieldEntityType.ASN]: { lineLabel: 'Line' },
};

/** True if the entity type has a detail level (placement DETAIL/BOTH are valid). */
export function isMasterDetailEntityType(entityType: CustomFieldEntityType): boolean {
  return entityType in MASTER_DETAIL_ENTITY_TYPES;
}

/** DevExtreme grid column `dataType` a custom field maps to. */
export type CustomFieldGridDataType = 'string' | 'number' | 'boolean' | 'date';

// ════════════════════════════════════════════════════════════════════════
// APPLICABILITY SCOPE — which instances of an entity a field applies to
// ════════════════════════════════════════════════════════════════════════

/**
 * How a custom field definition is scoped within its entity type. `GLOBAL`
 * applies to every instance; the other values scope to a "master" the instance
 * belongs to (e.g. a product's category, a location's warehouse).
 */
export enum CustomFieldScopeType {
  GLOBAL = 'GLOBAL',
  CATEGORY = 'CATEGORY',
  WAREHOUSE = 'WAREHOUSE',
}

/** One scope option a given entity type supports (drives the admin scope picker). */
export interface CustomFieldScopeOption {
  scopeType: CustomFieldScopeType;
  /** Human label for the master (e.g. "Category"). */
  label: string;
  /**
   * Whether the master forms a tree, so `scopeInherit` can cascade a field to
   * descendant masters (e.g. a category and its sub-categories).
   */
  hierarchical: boolean;
}

/**
 * The master scopes each entity type supports, beyond `GLOBAL`. Single source
 * of truth for the admin UI and the backend registry. Add an entry to scope a
 * new entity by a master.
 */
export const CUSTOM_FIELD_SCOPE_OPTIONS: Record<CustomFieldEntityType, CustomFieldScopeOption[]> = {
  [CustomFieldEntityType.PRODUCT]: [
    { scopeType: CustomFieldScopeType.CATEGORY, label: 'Category', hierarchical: true },
  ],
  [CustomFieldEntityType.LOCATION]: [
    { scopeType: CustomFieldScopeType.WAREHOUSE, label: 'Warehouse', hierarchical: false },
  ],
  [CustomFieldEntityType.ZONE]: [
    { scopeType: CustomFieldScopeType.WAREHOUSE, label: 'Warehouse', hierarchical: false },
  ],
  [CustomFieldEntityType.ASN]: [],
  // Client has no master within the tenant — GLOBAL scope only.
  [CustomFieldEntityType.CLIENT]: [],
};

/**
 * The scope-id chain of a specific entity instance, keyed by scope type. For a
 * hierarchical master the array is `[self, ...ancestors]`; for a flat master it
 * is `[self]`. `GLOBAL` is implicit (always matches). Built server-side from the
 * instance's master id (see ScopeChainService).
 */
export type CustomFieldScopeChain = Partial<Record<CustomFieldScopeType, string[]>>;

/** The scope attributes carried on a definition (subset used by the matcher). */
export interface CustomFieldScopeRef {
  scopeType: CustomFieldScopeType;
  scopeId?: string | null;
  scopeInherit: boolean;
}

/**
 * Whether a definition applies to an instance with the given scope chain.
 * Pure — reused by the backend validator/endpoint and the frontend. Per-instance
 * overrides layer on top via {@link isDefinitionApplicableToInstance}.
 */
export function isDefinitionInScope(
  def: CustomFieldScopeRef,
  chain: CustomFieldScopeChain
): boolean {
  if (def.scopeType === CustomFieldScopeType.GLOBAL) return true;
  if (!def.scopeId) return false;
  const ids = chain[def.scopeType] ?? [];
  // Inheriting defs match anywhere in the chain (self or an ancestor); otherwise
  // only the instance's direct master (chain head).
  return def.scopeInherit ? ids.includes(def.scopeId) : ids[0] === def.scopeId;
}

/**
 * Per-instance overrides of a field's applicability, keyed by field `key`:
 * `true` force-shows a field on this instance even if out of scope; `false`
 * hides it even if in scope; an absent key falls back to the scope result.
 */
export type CustomFieldOverrides = Record<string, boolean>;

/** The scope subset plus the field key needed to resolve per-instance applicability. */
export type CustomFieldApplicabilityRef = CustomFieldScopeRef & { key: string };

/**
 * Final applicability of a definition to a specific instance: a per-instance
 * override wins; otherwise fall back to scope. The hybrid model's single rule,
 * shared by the backend validator and the frontend form.
 */
export function isDefinitionApplicableToInstance(
  def: CustomFieldApplicabilityRef,
  chain: CustomFieldScopeChain,
  overrides: CustomFieldOverrides
): boolean {
  const override = overrides[def.key];
  if (override === true) return true;
  if (override === false) return false;
  return isDefinitionInScope(def, chain);
}

// ════════════════════════════════════════════════════════════════════════
// DESCRIPTOR TABLE — the DRY backbone
// ════════════════════════════════════════════════════════════════════════

/**
 * Everything the rest of the system needs to know about a field type, in one
 * place. Consumed by the backend validator (builds Zod), the mobile SDUI mapper
 * (`sduiComponent`), the web grid (`gridDataType`), and the algorithm catalog
 * merge (`algorithmFieldType`).
 */
export interface CustomFieldTypeDescriptor {
  type: CustomFieldType;
  /** Runtime primitive of a stored value (informational). */
  jsPrimitive: 'string' | 'number' | 'boolean' | 'string[]';
  /** SDUI component the mobile/handheld renderer uses for input. */
  sduiComponent: SDUIComponentType;
  /** Whether the input is multi-select (SELECT with multiple values). */
  multiSelect: boolean;
  /** DevExtreme grid column `dataType`. */
  gridDataType: CustomFieldGridDataType;
  /** Whether this type carries a fixed option set (LIST / MULTI_LIST). */
  hasOptions: boolean;
  /**
   * Algorithm-engine catalog field type, or `null` when the type cannot be
   * exposed to JEXL (no array primitive). MULTI_LIST is `null` — it is excluded
   * from the algorithm catalog rather than lossily coerced.
   */
  algorithmFieldType: AlgorithmFieldType | null;
}

export const CUSTOM_FIELD_TYPE_DESCRIPTORS: Record<CustomFieldType, CustomFieldTypeDescriptor> = {
  [CustomFieldType.TEXT]: {
    type: CustomFieldType.TEXT,
    jsPrimitive: 'string',
    sduiComponent: 'TEXT_INPUT',
    multiSelect: false,
    gridDataType: 'string',
    hasOptions: false,
    algorithmFieldType: 'string',
  },
  [CustomFieldType.NUMBER]: {
    type: CustomFieldType.NUMBER,
    jsPrimitive: 'number',
    sduiComponent: 'NUMBER_INPUT',
    multiSelect: false,
    gridDataType: 'number',
    hasOptions: false,
    algorithmFieldType: 'number',
  },
  [CustomFieldType.BOOLEAN]: {
    type: CustomFieldType.BOOLEAN,
    jsPrimitive: 'boolean',
    sduiComponent: 'SWITCH',
    multiSelect: false,
    gridDataType: 'boolean',
    hasOptions: false,
    algorithmFieldType: 'boolean',
  },
  [CustomFieldType.DATE]: {
    type: CustomFieldType.DATE,
    jsPrimitive: 'string',
    sduiComponent: 'DATE_PICKER',
    multiSelect: false,
    gridDataType: 'date',
    hasOptions: false,
    // DATE is exposed to the engine as epoch-ms `number` (see buildCustomFieldPrimitives).
    algorithmFieldType: 'number',
  },
  [CustomFieldType.LIST]: {
    type: CustomFieldType.LIST,
    jsPrimitive: 'string',
    sduiComponent: 'SELECT',
    multiSelect: false,
    gridDataType: 'string',
    hasOptions: true,
    algorithmFieldType: 'enum',
  },
  [CustomFieldType.MULTI_LIST]: {
    type: CustomFieldType.MULTI_LIST,
    jsPrimitive: 'string[]',
    sduiComponent: 'SELECT',
    multiSelect: true,
    gridDataType: 'string',
    hasOptions: true,
    algorithmFieldType: null,
  },
};

/** Human labels for the field-type picker in the admin UI. */
export const CustomFieldTypeLabels: Record<CustomFieldType, string> = {
  [CustomFieldType.TEXT]: 'Text',
  [CustomFieldType.NUMBER]: 'Number',
  [CustomFieldType.BOOLEAN]: 'Yes / No',
  [CustomFieldType.DATE]: 'Date',
  [CustomFieldType.LIST]: 'List (single select)',
  [CustomFieldType.MULTI_LIST]: 'List (multi select)',
};

/**
 * Whether a field type can be referenced from algorithm filters/scoring/branches.
 * Derived from the descriptor table so there is no second list to keep in sync.
 */
export function isAlgorithmExposable(type: CustomFieldType): boolean {
  return CUSTOM_FIELD_TYPE_DESCRIPTORS[type].algorithmFieldType !== null;
}

// ════════════════════════════════════════════════════════════════════════
// SHARED CONSTANTS
// ════════════════════════════════════════════════════════════════════════

/**
 * Prefix applied to a custom field key when it is exposed to the algorithm
 * engine catalog (`cf_<key>`). Guarantees no collision with built-in catalog
 * keys and keeps the identifier JEXL-safe.
 */
export const CUSTOM_FIELD_ALGORITHM_PREFIX = 'cf_';

/**
 * A custom field key must be a lowercase identifier so that `cf_<key>` compiles
 * as a JEXL identifier and `custom_fields[key]` access is unambiguous. Enforced
 * by the backend definition service and the admin UI form.
 */
export const CUSTOM_FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

/** Maximum length of a custom field key. */
export const CUSTOM_FIELD_KEY_MAX_LENGTH = 60;

// ════════════════════════════════════════════════════════════════════════
// VALUE & DEFINITION SHAPES
// ════════════════════════════════════════════════════════════════════════

/** A stored custom field value. */
export type CustomFieldValue = string | number | boolean | string[] | null;

/** The `custom_fields` jsonb bag carried on an entity, keyed by field `key`. */
export type CustomFieldValues = Record<string, CustomFieldValue>;

/** One selectable option for LIST / MULTI_LIST fields. */
export interface CustomFieldOption {
  value: string;
  label: string;
}

/** Validation constraints authored per field (persisted as jsonb). */
export interface CustomFieldValidation {
  required?: boolean;
  /** TEXT → min/max string length. NUMBER → min/max value. */
  min?: number;
  max?: number;
  /** TEXT only: a regular-expression source the value must match. */
  regex?: string;
}

/**
 * A user-authored custom field declaration. Mirrors a backend
 * `wms_custom_field_definitions` row and is consumed by web + mobile.
 */
export interface CustomFieldDefinitionDto {
  id: string;
  entityType: CustomFieldEntityType;
  /**
   * Stable key used in `custom_fields[key]` and the `cf_<key>` catalog id.
   * Immutable after creation.
   */
  key: string;
  label: string;
  fieldType: CustomFieldType;
  /** Header (MASTER), each line (DETAIL), or both. Defaults to MASTER. */
  placement: CustomFieldPlacement;
  /** Present for LIST / MULTI_LIST. */
  options?: CustomFieldOption[];
  validation?: CustomFieldValidation;
  defaultValue?: CustomFieldValue;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  /** Applicability scope (default GLOBAL = every instance). */
  scopeType: CustomFieldScopeType;
  /** The master instance id when `scopeType` is not GLOBAL. */
  scopeId?: string | null;
  /** For a hierarchical master, whether descendants inherit the field. */
  scopeInherit: boolean;
}
